import React, { forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { useScript } from '../../../../../../hooks/use-script';
import useGetBrightbackData, { SS_BRIGHTBACK_DOMAIN_NAME } from './hooks/use-get-brightback-data';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { CANCEL_SUBSCRIPTION_LINK_CLICKED } from '../../../../../../constants/analytics-events';
import { Button } from 'ui-components/components/button';
import CancelWithinShipStationButton from '../cancel-subscription-button';
import styles from '../../../style.module.less';

const BRIGHTBACK_LIBRARY_SOURCE = '//app.brightback.com/js/current/brightback.js';

const BRIGHTBACK_ERROR = {
  noLibrary: 'unable to attach library',
  unsafeToSendUser: 'invalid response',
  unhandledError: 'unhandled error response',
};

const BrightbackButton = forwardRef(({ children }, ref) => {
  useScript(BRIGHTBACK_LIBRARY_SOURCE, ref);
  const brightbackData = useGetBrightbackData();
  const [brightbackError, setBrightbackError] = useAsyncSafeState();

  const onClick = useCallback(() => {
    if (!window.Brightback) {
      setBrightbackError(BRIGHTBACK_ERROR.noLibrary);
      return;
    }

    Segment.trackEvent(CANCEL_SUBSCRIPTION_LINK_CLICKED, {
      source: 'settings-accounts-subscription cancel subscription brightback link',
      ...brightbackData,
    });

    const brightbatchRequest = window.Brightback?.handleDataPromise(brightbackData);
    brightbatchRequest
      .then(({ valid, url }) => {
        if (valid && url?.includes(SS_BRIGHTBACK_DOMAIN_NAME)) {
          window.location.href = url;
          return;
        }
        setBrightbackError(`${BRIGHTBACK_ERROR.unsafeToSendUser} - ${url}`);
      })
      .catch(() => {
        setBrightbackError(BRIGHTBACK_ERROR.unhandledError);
      });
  }, [brightbackData, setBrightbackError]);

  if (brightbackError) {
    return <CancelWithinShipStationButton brightbackError={brightbackError}>{children}</CancelWithinShipStationButton>;
  }

  return (
    <Button className={styles.small} onClick={onClick} theme='link'>
      {children}
    </Button>
  );
});

BrightbackButton.propTypes = {
  children: PropTypes.node.isRequired,
};

export default BrightbackButton;
